import type { OfferOptionsSelection, OfferOptionsSelectionPreview } from './queries';

/**
 * Qué muestra el modal como total antes del hold cuando la oferta tiene
 * opciones, y si el hold puede salir. El total con la selección lo cotiza el
 * proveedor (vía el backend); acá NO se suma nada: se decide QUÉ número se
 * pinta y con qué autoridad, atando el resultado del preview a la selección
 * VIVA — un total cotizado para otra selección u otra oferta no se presenta
 * como el actual (regla de UI post-mutación: el registro autoritativo manda,
 * lo stale se marca o se deshabilita).
 *
 * - `quoting`: la selección viva todavía no tiene su cotización (debounce,
 *   llamada en vuelo, o `data` de una key anterior). Se pinta el precio de la
 *   oferta marcado como no vigente y el hold NO sale hasta que llegue.
 * - `unavailable`: no hubo cotización autoritativa (backend `unavailable`,
 *   error). Se pinta el precio de la oferta como NO cotizado con la selección
 *   y se avisa; el hold sale — el hold mismo cotiza con autoridad.
 * - `rejected`: el operador rechazó ESTA selección: se muestra el motivo y
 *   el hold no sale.
 * - `null` (autoritativo): `total.final` del backend para la selección viva.
 */
export type SelectionPreviewNotice = 'quoting' | 'unavailable' | 'rejected';

export interface SelectionPreviewDisplay {
  /** Importe que se pinta como total (minor units). */
  amountMinor?: number;
  /** true = es el total del backend para la selección viva. */
  authoritative: boolean;
  notice: SelectionPreviewNotice | null;
  holdBlocked: boolean;
}

/** Display only: do not present the base price as a total including add-ons. */
export function showUnquotedOptionalItemsPrice({
  isPostHold, hasOptionalItems, offerQuoteAvailable, display,
}: {
  isPostHold: boolean;
  hasOptionalItems: boolean;
  offerQuoteAvailable: boolean;
  display: SelectionPreviewDisplay;
}): boolean {
  return !isPostHold && hasOptionalItems && !display.authoritative &&
    (!offerQuoteAvailable || display.notice === 'unavailable');
}

/**
 * Si la selección cambia lo comercial de la oferta. Se comparte entre el
 * preview y la evidencia de reprecio: los dos tienen que decidir sobre la
 * MISMA selección, o el precio base puede terminar comparado contra un hold
 * que ya incluye adicionales.
 */
export function offerSelectionHasChoices(
  selection: OfferOptionsSelection,
): boolean {
  return (
    Boolean(selection.pickupId) ||
    (selection.ticketQuantities?.length ?? 0) > 0 ||
    Boolean(selection.sessionId) ||
    (selection.accommodationIds?.length ?? 0) > 0 ||
    Boolean(selection.ticketTypeId) ||
    (selection.optionalItemCodes?.length ?? 0) > 0 ||
    (selection.occupancy?.rooms.length ?? 0) > 0 ||
    (selection.supplements?.length ?? 0) > 0
  );
}

/**
 * Solo el preview autoritativo de la key vigente puede prestar su token al
 * hold. Durante debounce/refetch, `keepPreviousData` puede seguir exponiendo
 * el resultado anterior: `display.authoritative` lo deja fuera.
 */
export function authoritativeQuoteTokenForHold(
  display: SelectionPreviewDisplay,
  preview: OfferOptionsSelectionPreview | undefined,
): string | undefined {
  return display.authoritative &&
    preview?.state === 'priced' &&
    typeof preview.quoteToken === 'string' &&
    preview.quoteToken.length > 0
    ? preview.quoteToken
    : undefined;
}

/**
 * Un preview pendiente o rechazado bloquea cualquier hold. La única excepción
 * es Tours después de congelar un quote autoritativo completo: en esa etapa el
 * preview vivo queda deshabilitado y manda el snapshot quote -> hold.
 */
export function selectionPreviewAllowsHold(
  display: SelectionPreviewDisplay,
  hasFrozenAuthoritativeQuote = false,
): boolean {
  return hasFrozenAuthoritativeQuote || !display.holdBlocked;
}

export function shouldRefreshCircuitQuoteAfterHoldError(
  vertical: string,
  errorCode: string | undefined,
): boolean {
  return vertical === 'circuits' && errorCode === 'RUTERO_HOLD_QUOTE_MISMATCH';
}

export function isOfferPreviewEnabled({
  offerQuoteAvailable,
  hasOptions,
  hasOptionalItems,
  hasBooking,
}: {
  offerQuoteAvailable: boolean;
  hasOptions: boolean;
  hasOptionalItems: boolean;
  hasBooking: boolean;
}): boolean {
  return offerQuoteAvailable && (hasOptions || hasOptionalItems) && !hasBooking;
}

/**
 * Misma selección = misma sesión, mismo multiconjunto de plazas, misma tarifa
 * y mismo multiconjunto de adicionales (#2383: repetir un código = más unidades, el
 * orden no cambia la selección).
 */
export function selectionsEqual(a: OfferOptionsSelection, b: OfferOptionsSelection): boolean {
  const multiset = (values: string[] | undefined) => [...(values ?? [])].sort().join(' ');
  const quantities = (values: OfferOptionsSelection['ticketQuantities']) =>
    [...(values ?? [])]
      .sort((left, right) => left.id.localeCompare(right.id))
      .map(({ id, quantity }) => `${id}:${quantity}`)
      .join(' ');
  const occupancy = (value: OfferOptionsSelection['occupancy']) =>
    JSON.stringify(
      value?.rooms.map((room) => ({
        ages: room.ages,
        categoryId: room.categoryId ?? '',
      })) ?? [],
    );
  const supplements = (values: OfferOptionsSelection['supplements']) =>
    JSON.stringify(
      [...(values ?? [])]
        .map((entry) => ({
          code: entry.code,
          quantity: entry.quantity ?? 1,
          roomIndex: entry.roomIndex ?? null,
          passengerIndex: entry.passengerIndex ?? null,
          transfer: entry.transfer
            ? {
              airport: entry.transfer.airport,
              airline: entry.transfer.airline ?? '',
              flightNumber: entry.transfer.flightNumber,
              dateTime: entry.transfer.dateTime,
              city: entry.transfer.city ?? '',
            }
            : null,
        }))
        .sort((left, right) =>
          `${left.code}:${left.roomIndex ?? ''}:${left.passengerIndex ?? ''}`.localeCompare(
            `${right.code}:${right.roomIndex ?? ''}:${right.passengerIndex ?? ''}`,
          )),
    );
  return (
    (a.pickupId || '') === (b.pickupId || '') &&
    quantities(a.ticketQuantities) === quantities(b.ticketQuantities) &&
    (a.sessionId || '') === (b.sessionId || '') &&
    multiset(a.accommodationIds) === multiset(b.accommodationIds) &&
    (a.ticketTypeId || '') === (b.ticketTypeId || '') &&
    multiset(a.optionalItemCodes) === multiset(b.optionalItemCodes) &&
    occupancy(a.occupancy) === occupancy(b.occupancy) &&
    supplements(a.supplements) === supplements(b.supplements)
  );
}

export function resolveSelectionPreviewDisplay({
  enabled,
  contextSettled = true,
  liveSelection,
  quotedSelection,
  preview,
  isFetching,
  isPreviousData,
  isError,
  errorCode,
  offerFinalMinor,
  requiresAuthoritativeQuote = false,
}: {
  /** La oferta tiene opciones y se pide preview (ferry/buses con opciones cargadas). */
  enabled: boolean;
  /** La foto completa selection + passengers ya atravesó el debounce. */
  contextSettled?: boolean;
  /** Lo que el asesor tiene elegido AHORA. */
  liveSelection: OfferOptionsSelection;
  /** La selección con la que se pidió el preview vigente (tras el debounce). */
  quotedSelection: OfferOptionsSelection;
  preview: OfferOptionsSelectionPreview | undefined;
  isFetching: boolean;
  /** `keepPreviousData`: `preview` es de una key anterior (otra selección u oferta). */
  isPreviousData: boolean;
  isError: boolean;
  errorCode?: string;
  /** Precio de venta de la oferta sin opciones (lo que ya se mostraba). */
  offerFinalMinor?: number;
  /** Una card sin precio no puede continuar sin el total y token del quote. */
  requiresAuthoritativeQuote?: boolean;
}): SelectionPreviewDisplay {
  if (!enabled) {
    return {
      amountMinor: offerFinalMinor,
      authoritative: false,
      notice: null,
      holdBlocked: requiresAuthoritativeQuote,
    };
  }
  if (
    !contextSettled ||
    !selectionsEqual(liveSelection, quotedSelection) ||
    isFetching ||
    isPreviousData ||
    (!preview && !isError)
  ) {
    return {
      amountMinor: offerFinalMinor,
      authoritative: false,
      notice: 'quoting',
      holdBlocked: true,
    };
  }
  if (isError || !preview || preview.state === 'unavailable') {
    // Sin nada elegido, el precio de la oferta ES el total: no hay nada que
    // avisar (un proveedor que no cotiza opciones no tiene que llenar el modal
    // de avisos al abrirlo). Con selección, sí: el total de arriba no la
    // incluye.
    const hasSelection = offerSelectionHasChoices(liveSelection);
    return {
      amountMinor: offerFinalMinor,
      authoritative: false,
      notice: hasSelection ? 'unavailable' : null,
      holdBlocked:
        requiresAuthoritativeQuote ||
        (hasSelection && errorCode === 'TOUR_SELECTION_UNAVAILABLE'),
    };
  }
  if (preview.state === 'rejected') {
    return {
      amountMinor: offerFinalMinor,
      authoritative: false,
      notice: 'rejected',
      holdBlocked: true,
    };
  }
  return {
    amountMinor: preview.total?.final.amountMinor ?? offerFinalMinor,
    authoritative: preview.total !== null,
    notice: null,
    holdBlocked: requiresAuthoritativeQuote && preview.total === null,
  };
}
